import { useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2, Send, Mail, Paperclip, X, MessageSquare, Reply } from 'lucide-react';
import { toast } from 'sonner';

interface Attachment {
  id: string;
  file_name: string;
  file_size: number;
  file_path: string;
}

interface Message {
  id: string;
  company_id: string;
  sender_id: string;
  sender_role: string;
  subject: string;
  body: string;
  is_read: boolean;
  created_at: string;
  parent_id?: string;
  thread_depth?: number;
  category?: string;
  attachment_count?: number;
}

interface InvestorMessagesProps {
  companyId: string;
  messages: Message[];
  onRefresh: () => void;
}

export function InvestorMessages({ companyId, messages, onRefresh }: InvestorMessagesProps) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Organize messages into threads
  const threadMap = new Map<string, Message[]>();
  const rootMessages: Message[] = [];

  messages.forEach((msg) => {
    if (!msg.parent_id) {
      rootMessages.push(msg);
    } else {
      if (!threadMap.has(msg.parent_id)) {
        threadMap.set(msg.parent_id, []);
      }
      threadMap.get(msg.parent_id)!.push(msg);
    }
  });

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter((f) => f.size <= 10 * 1024 * 1024); // 10MB limit

    if (validFiles.length < files.length) {
      toast.error('Some files exceed 10MB limit');
    }

    setAttachments((prev) => [...prev, ...validFiles]);
  }

  function removeAttachment(index: number) {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }

  async function uploadAttachments(messageId: string): Promise<void> {
    if (attachments.length === 0) return;

    setUploadingFiles(true);
    try {
      for (const file of attachments) {
        const filePath = `${companyId}/${messageId}/${Date.now()}-${file.name}`;

        const { error: uploadError } = await supabase.storage
          .from('message-attachments')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { error: dbError } = await supabase.from('message_attachments').insert({
          message_id: messageId,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          mime_type: file.type,
          uploaded_by: (await supabase.auth.getUser()).data.user?.id,
        });

        if (dbError) throw dbError;
      }
    } catch (error) {
      console.error('Error uploading attachments:', error);
      throw error;
    } finally {
      setUploadingFiles(false);
    }
  }

  async function sendMessage() {
    if (!subject || !body) {
      toast.error('Subject and message required');
      return;
    }

    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-investor-message', {
        body: {
          companyId,
          subject,
          body,
          recipientRole: 'company',
          parentId: replyingTo,
        },
      });

      if (error) throw error;

      const messageId = data?.messageId;
      if (messageId && attachments.length > 0) {
        await uploadAttachments(messageId);
      }

      // Trigger smart categorization
      if (messageId) {
        supabase.functions
          .invoke('categorize-message', {
            body: { messageId, subject, body },
          })
          .catch((err) => console.error('Categorization failed:', err));
      }

      toast.success(replyingTo ? 'Reply sent successfully' : 'Message sent successfully');
      setSubject('');
      setBody('');
      setReplyingTo(null);
      setAttachments([]);
      onRefresh();
    } catch (error: any) {
      toast.error(error.message || 'Failed to send message');
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  }

  async function markRead(id: string) {
    try {
      const { error } = await supabase.functions.invoke('mark-message-read', {
        body: { id },
      });

      if (error) throw error;
      onRefresh();
    } catch (error: any) {
      toast.error('Failed to mark message as read');
      console.error('Error marking message as read:', error);
    }
  }

  function MessageThread({ message, depth = 0 }: { message: Message; depth?: number }) {
    const replies = threadMap.get(message.id) || [];

    return (
      <div className={depth > 0 ? 'ml-8 mt-2 border-l-2 border-border pl-4' : ''}>
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{message.subject}</span>
                {message.category && (
                  <Badge variant="secondary" className="text-xs">
                    {message.category}
                  </Badge>
                )}
                {message.attachment_count! > 0 && (
                  <Badge variant="outline" className="text-xs">
                    <Paperclip className="h-3 w-3 mr-1" />
                    {message.attachment_count}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{message.body}</p>
              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                <span>{message.sender_role === 'investor' ? 'You' : 'Company'}</span>
                <span>•</span>
                <span>{new Date(message.created_at).toLocaleString()}</span>
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setReplyingTo(message.id);
                setSubject(`Re: ${message.subject}`);
              }}
            >
              <Reply className="h-3 w-3 mr-1" />
              Reply
            </Button>
          </div>
        </div>

        {replies.length > 0 && (
          <div className="mt-3 space-y-2">
            {replies.map((reply) => (
              <MessageThread key={reply.id} message={reply} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Investor Q&A
          {replyingTo && (
            <Badge variant="secondary" className="text-xs">
              Replying...
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Compose Section */}
        <div className="space-y-3 p-4 rounded-lg border border-border bg-background">
          {replyingTo && (
            <div className="flex items-center justify-between text-sm bg-accent/50 p-2 rounded">
              <span className="flex items-center gap-2">
                <Reply className="h-4 w-4" />
                Replying to thread
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setReplyingTo(null);
                  setSubject('');
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
          <h3 className="text-sm font-medium">{replyingTo ? 'Your Reply' : 'Send a Message'}</h3>
          {!replyingTo && (
            <Input
              placeholder="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="bg-card"
            />
          )}
          <Textarea
            placeholder="Ask a question or share a comment…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            className="bg-card"
          />

          {/* Attachments */}
          {attachments.length > 0 && (
            <div className="space-y-2">
              {attachments.map((file, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between text-sm bg-accent/30 p-2 rounded"
                >
                  <span className="flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    {file.name} ({Math.round(file.size / 1024)}KB)
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => removeAttachment(idx)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelect}
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || uploadingFiles}
            >
              <Paperclip className="h-4 w-4 mr-1" />
              Attach
            </Button>
            <Button
              onClick={sendMessage}
              disabled={sending || uploadingFiles || !subject || !body}
              className="flex-1"
            >
              {sending || uploadingFiles ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  {uploadingFiles ? 'Uploading...' : 'Sending...'}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send {replyingTo ? 'Reply' : 'Message'}
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Message Threads */}
        <div>
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Conversations ({rootMessages.length})
          </h3>
          {rootMessages.length > 0 ? (
            <div className="space-y-4">
              {rootMessages.map((message) => (
                <div
                  key={message.id}
                  className="rounded-lg border border-border p-4 bg-background hover:bg-accent/30 transition-colors"
                >
                  <MessageThread message={message} />
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center border border-dashed border-border rounded-lg">
              <Mail className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-sm text-muted-foreground">No messages yet.</p>
              <p className="text-xs text-muted-foreground mt-1">
                Send your first message using the form above.
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
